import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import {
  getPeriodicReview,
  getPeriodicReviewAml,
  getPeriodicReviewConfirmation,
  getPeriodicReviewIdentity,
  getPeriodicReviewNationalClientIdentifier,
  postPeriodicReviewAml,
  postPeriodicReviewConfirmation,
  postPeriodicReviewIdentity,
  postPeriodicReviewNationalClientIdentifier,
} from '@/services/api/profile/periodic-review';
import { parseProfileIdentity } from './use-profile-identity';
import { userAttributesQueryKey } from './use-user-attributes';

const PERIODIC_REVIEW_QUERY_KEY_PREFIX = 'periodic-review';

const PERIODIC_REVIEW_CONFIRMATION_MUTATION_KEY = ['profile', `${PERIODIC_REVIEW_QUERY_KEY_PREFIX}-confirmation`, 'post'];

const profilePeriodicReviewQueryKey = (pid: UserPid) => ['profile', PERIODIC_REVIEW_QUERY_KEY_PREFIX, pid];

const profilePeriodicReviewConfirmationQueryKey = (pid: UserPid) => ['profile', `${PERIODIC_REVIEW_QUERY_KEY_PREFIX}-confirmation`, pid];

const profilePeriodicReviewIdentityQueryKey = (pid: UserPid) => ['profile', `${PERIODIC_REVIEW_QUERY_KEY_PREFIX}-identity`, pid];

const profilePeriodicReviewNationalClientIdentifierQueryKey = (pid: UserPid) => ['profile', `${PERIODIC_REVIEW_QUERY_KEY_PREFIX}-national-client-identifier`, pid];

const profilePeriodicReviewAmlQueryKey = (pid: UserPid) => ['profile', `${PERIODIC_REVIEW_QUERY_KEY_PREFIX}-aml`, pid];

const profilePeriodicReviewQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    queryFn: getPeriodicReview,
    queryKey: profilePeriodicReviewQueryKey(pid),
  });

const useProfilePeriodicReview = () => {
  const { pid } = useUserPid();

  return useQuery(profilePeriodicReviewQueryOptions(pid));
};

const useProfilePeriodicReviewConfirmation = (enabled = true) => {
  const { pid } = useUserPid();

  return useQuery({
    enabled: !!pid && enabled,
    queryFn: getPeriodicReviewConfirmation,
    queryKey: profilePeriodicReviewConfirmationQueryKey(pid),
  });
};

const profilePeriodicReviewIdentityQueryFn = async () => {
  const draft = await getPeriodicReviewIdentity();

  return {
    ...(await parseProfileIdentity(draft?.answers ?? null)),
    isAvailable: !!draft,
    locked: draft?.locked,
  };
};

const useProfilePeriodicReviewIdentity = (enabled = true) => {
  const { pid } = useUserPid();

  return useQuery({
    enabled: !!pid && enabled,
    queryFn: profilePeriodicReviewIdentityQueryFn,
    queryKey: profilePeriodicReviewIdentityQueryKey(pid),
  });
};

const useProfilePeriodicReviewIdentityMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postPeriodicReviewIdentity,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profilePeriodicReviewIdentityQueryKey(pid) }),
  });
};

const useProfilePeriodicReviewNationalClientIdentifier = (enabled = true) => {
  const { pid } = useUserPid();

  return useQuery({
    enabled: !!pid && enabled,
    queryFn: getPeriodicReviewNationalClientIdentifier,
    queryKey: profilePeriodicReviewNationalClientIdentifierQueryKey(pid),
  });
};

const useProfilePeriodicReviewNationalClientIdentifierMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postPeriodicReviewNationalClientIdentifier,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profilePeriodicReviewNationalClientIdentifierQueryKey(pid) }),
  });
};

const useProfilePeriodicReviewAml = (enabled = true) => {
  const { pid } = useUserPid();

  return useQuery({
    enabled: !!pid && enabled,
    queryFn: getPeriodicReviewAml,
    queryKey: profilePeriodicReviewAmlQueryKey(pid),
  });
};

const useProfilePeriodicReviewAmlMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postPeriodicReviewAml,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profilePeriodicReviewAmlQueryKey(pid) }),
  });
};

const useProfilePeriodicReviewConfirmationMutation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();

  return useMutation({
    mutationFn: postPeriodicReviewConfirmation,
    mutationKey: PERIODIC_REVIEW_CONFIRMATION_MUTATION_KEY,
    // A failed request may still have applied, or applied and failed on what follows, either of which moves the review on
    onSettled: () => {
      // Confirming changes what the live queries hold, and a stale verified status would skip the verification it may have reset
      queryClient.removeQueries({
        queryKey: ['profile'],
        predicate: ({ queryKey }) => !String(queryKey[1]).startsWith(PERIODIC_REVIEW_QUERY_KEY_PREFIX),
      });
      queryClient.invalidateQueries({ queryKey: userAttributesQueryKey(pid) });

      return Promise.all([
        queryClient.invalidateQueries({ queryKey: profilePeriodicReviewQueryKey(pid) }),
        queryClient.invalidateQueries({ queryKey: profilePeriodicReviewConfirmationQueryKey(pid) }),
      ]);
    },
  });
};

export {
  PERIODIC_REVIEW_CONFIRMATION_MUTATION_KEY,
  useProfilePeriodicReview,
  useProfilePeriodicReviewAml,
  useProfilePeriodicReviewAmlMutation,
  useProfilePeriodicReviewConfirmation,
  useProfilePeriodicReviewConfirmationMutation,
  useProfilePeriodicReviewIdentity,
  useProfilePeriodicReviewIdentityMutation,
  useProfilePeriodicReviewNationalClientIdentifier,
  useProfilePeriodicReviewNationalClientIdentifierMutation,
};
